/**
 * Decimal-string money arithmetic and formatting.
 *
 * Everything here works on **scaled integers** parsed from the string and renders
 * back to a string — no value is ever converted to a JS `number` and back, which
 * is the specific bug that turns a ledger total into the sponsor's "136.9%"
 * complaint.
 *
 * ## The schema has THREE precisions, not one
 *
 * This module originally assumed everything was two decimal places, and that was
 * wrong in a way that crashed the work-order screen: `toCents("50.0000")` threw,
 * the throw escaped a formatter during render, and React unmounted the tree to a
 * blank page. The database is explicit about it:
 *
 * | Kind | Columns | Scale |
 * |---|---|---|
 * | **Amount** | `cost_entry.amount`, `payment.amount`, `proposed_cost_line.amount` | **2** |
 * | **Rate / unit cost** | `cost_entry.posted_rate`, `cost_entry.unit_cost`, `labour_rate.hourly_rate` | **4** |
 * | **Quantity** | `cost_entry.quantity`, `inventory_txn.quantity` | **3** |
 *
 * Those scales are load-bearing, not incidental. A labour rate needs sub-cent
 * precision so that hours × rate lands on the right cent; a quantity needs
 * thirds of a unit for fluids. So each kind gets its own parser at its own scale,
 * and **each stays strict** — loosening `toCents` to accept four decimals would
 * have let a genuinely malformed ledger amount through silently, which is the
 * opposite of what these constraints are for.
 */

/** `cost_entry.amount` and friends — `numeric(10,2)`. */
export const AMOUNT_SCALE = 2;
/** `posted_rate`, `unit_cost`, `hourly_rate` — `numeric(10,4)`. */
export const RATE_SCALE = 4;
/** `quantity` — `numeric(12,3)`. */
export const QUANTITY_SCALE = 3;

const DECIMAL_RE = /^(-)?(\d+)(?:\.(\d+))?$/;

/**
 * Parse a decimal string into an integer at `scale` decimal places.
 *
 * Rejects more precision than `scale` rather than rounding it away: a value with
 * unexpected precision means the caller has the wrong kind of number in hand —
 * a rate where an amount belongs, say — and silently truncating it would hide
 * that at exactly the point where money is being computed.
 */
export function parseScaled(value: string, scale: number): number {
  const match = DECIMAL_RE.exec(value.trim());
  if (!match) {
    throw new Error(`not a decimal value: ${JSON.stringify(value)}`);
  }
  const [, sign, whole, frac = ""] = match;
  if (frac.length > scale) {
    throw new Error(
      `${JSON.stringify(value)} has ${frac.length} decimal places, but this is a ` +
        `scale-${scale} value. Use the parser for its own kind — amounts are scale ` +
        `${AMOUNT_SCALE}, rates and unit costs ${RATE_SCALE}, quantities ${QUANTITY_SCALE}.`,
    );
  }
  const factor = 10 ** scale;
  const scaled = Number(whole) * factor + Number(frac.padEnd(scale, "0") || "0");
  if (!Number.isSafeInteger(scaled)) throw new Error(`value out of range: ${value}`);
  return sign === "-" ? -scaled : scaled;
}

/** Render a scaled integer back to a decimal string with exactly `scale` places. */
export function formatScaled(scaled: number, scale: number): string {
  if (!Number.isInteger(scaled)) throw new Error(`expected an integer: ${scaled}`);
  const negative = scaled < 0;
  const abs = Math.abs(scaled);
  const factor = 10 ** scale;
  const body =
    scale === 0
      ? String(abs)
      : `${Math.floor(abs / factor)}.${String(abs % factor).padStart(scale, "0")}`;
  return negative ? `-${body}` : body;
}

/** Parse a ledger **amount** to integer cents. Strictly two decimal places. */
export function toCents(amount: string): number {
  return parseScaled(amount, AMOUNT_SCALE);
}

/** Parse a **rate or unit cost** to integer ten-thousandths. */
export function toRateUnits(rate: string): number {
  return parseScaled(rate, RATE_SCALE);
}

/** Parse a **quantity** to integer thousandths. */
export function toQuantityUnits(quantity: string): number {
  return parseScaled(quantity, QUANTITY_SCALE);
}

/**
 * Quantity × unit cost, as the line amount the ledger will hold — or null.
 *
 * `cost_entry_arithmetic_chk` requires `amount = round(quantity * unit_cost, 2)`
 * whenever both are set (or a variance note on the line), so this is the figure
 * to propose for a line and the one to compare a typed line against. The invoice
 * form fills its line total with it and `varianceReasonFor` checks with it, so the
 * two cannot disagree about a line.
 *
 * It is EXACT. A quantity is scale 3 and a unit cost scale 4, so the product is a
 * whole number of ten-millionths, held as a BigInt because a 12-digit quantity
 * times a 10-digit cost outgrows the range a double holds exactly. It rounds half
 * AWAY FROM ZERO, as Postgres's `round(numeric, 2)` does. The floating-point
 * version does not: `1.005 * 100` is `100.49999…` in binary, so
 * `Math.round(x * 100) / 100` says 1.00 where the database says 1.01, and a line
 * typed as 1.00 then passes a float check and fails the CHECK at confirm.
 *
 * Null rather than a throw for anything that is not a decimal at its own scale:
 * the first caller is a form mid-keystroke, where "" and "2" before the price is
 * typed are states a person passes through, not errors. There is no range check —
 * a caller that must fit a column tests the result against it.
 */
export function lineAmountFor(quantity: string, unitCost: string): string | null {
  const cents = lineCentsFor(quantity, unitCost);
  return cents === null ? null : centsToAmount(cents);
}

/** `lineAmountFor` in signed cents, for a caller that has to compare or add. */
function lineCentsFor(quantity: string, unitCost: string): bigint | null {
  const q = scaledBigIntOrNull(quantity, QUANTITY_SCALE);
  const u = scaledBigIntOrNull(unitCost, RATE_SCALE);
  if (q === null || u === null) return null;
  const product = q * u;
  const divisor = 10n ** BigInt(QUANTITY_SCALE + RATE_SCALE - AMOUNT_SCALE);
  const negative = product < 0n;
  const magnitude = negative ? -product : product;
  let cents = magnitude / divisor;
  if ((magnitude % divisor) * 2n >= divisor) cents += 1n;
  return negative ? -cents : cents;
}

function centsToAmount(cents: bigint): string {
  const negative = cents < 0n;
  const magnitude = negative ? -cents : cents;
  const body = `${magnitude / 100n}.${(magnitude % 100n).toString().padStart(2, "0")}`;
  return negative && magnitude !== 0n ? `-${body}` : body;
}

/**
 * Dollars for a message, from BigInt cents. Not `formatMoney`: that parses through
 * a double and throws past 2^53, and the amounts this names are the ones too
 * large for the column, so the sentence explaining the refusal would itself fail.
 */
function dollarsFromCents(cents: bigint): string {
  const negative = cents < 0n;
  const magnitude = negative ? -cents : cents;
  const whole = (magnitude / 100n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${negative ? "-" : ""}$${whole}.${(magnitude % 100n).toString().padStart(2, "0")}`;
}

/**
 * The largest amount a `numeric(10,2)` holds, in cents: $99,999,999.99.
 *
 * Every purchase-order money column is `numeric(10,2)`, and so is the total the
 * approval routine adds up (`v_total`). A body bounded only by its own fields can
 * still produce more: quantity allows nine integer digits and unit cost six.
 */
export const NUMERIC_10_2_MAX_CENTS = 9_999_999_999n;

/**
 * Why this purchase order's money would not fit its columns, or null when it
 * does.
 *
 * A line's quantity × unit cost lands in `purchase_order_line.extended_cost`, and
 * the lines plus their tax, shipping and order tax become the approval total. Too
 * large for either used to fail inside Postgres as 22003 and answer 500 ("Could
 * not save the purchase order") for a request every field rule accepted. A line
 * that does not parse is left to the schema, which already refuses it.
 */
export function purchaseOrderAmountProblem(order: {
  lines: readonly { quantity: string; unitCost: string; taxAmount?: string | null }[];
  shippingAmount?: string | null;
  taxAmount?: string | null;
}): string | null {
  const ceiling = dollarsFromCents(NUMERIC_10_2_MAX_CENTS);
  const cents = (amount: string | null | undefined) =>
    amount === null || amount === undefined ? 0n : (scaledBigIntOrNull(amount, AMOUNT_SCALE) ?? 0n);
  let total = cents(order.shippingAmount) + cents(order.taxAmount);
  for (const [index, line] of order.lines.entries()) {
    const extended = lineCentsFor(line.quantity, line.unitCost);
    if (extended === null) continue;
    if (extended > NUMERIC_10_2_MAX_CENTS) {
      return (
        `Line ${index + 1} comes to ${dollarsFromCents(extended)}, ` +
        `more than a purchase-order line can hold (${ceiling}).`
      );
    }
    total += extended + cents(line.taxAmount);
  }
  if (total > NUMERIC_10_2_MAX_CENTS) {
    return (
      `The order comes to ${dollarsFromCents(total)}, ` +
      `more than a purchase order can hold (${ceiling}). Split it into more than one order.`
    );
  }
  return null;
}

/**
 * A decimal string rounded to `scale` places, exactly, or null when it is not a
 * decimal at all.
 *
 * Half AWAY FROM ZERO, as Postgres's `round(numeric, n)` does and as
 * `lineAmountFor` above does, so a figure rounded here and a product rounded
 * there agree. BigInt throughout: `Number("2.675").toFixed(2)` is "2.67" in
 * binary floating point, and the database says 2.68.
 *
 * The result carries exactly `scale` places ("1.000" at 2 is "1.00"), keeps its
 * sign, and never reads "-0.00": a value that rounds to zero is zero.
 */
export function roundDecimal(value: string, scale: number): string | null {
  const match = DECIMAL_RE.exec(value.trim());
  if (!match) return null;
  const [, sign, whole = "0", frac = ""] = match;
  const digits = BigInt(whole + frac);
  const extra = frac.length - scale;
  let scaled: bigint;
  if (extra <= 0) {
    scaled = digits * 10n ** BigInt(-extra);
  } else {
    const divisor = 10n ** BigInt(extra);
    scaled = digits / divisor;
    if ((digits % divisor) * 2n >= divisor) scaled += 1n;
  }
  const factor = 10n ** BigInt(scale);
  const body =
    scale === 0
      ? scaled.toString()
      : `${scaled / factor}.${(scaled % factor).toString().padStart(scale, "0")}`;
  return sign === "-" && scaled !== 0n ? `-${body}` : body;
}

/**
 * Decimal places an invoice line's quantity, unit price and total carry on the
 * entry forms and in the manual-invoice body — Eden, 2 October 2026: *"all the
 * number fields here only need 2 decimal places. individual price and total
 * price are currency so they only need 2, and for quantity I don't think we
 * would have any instance of needing to record the amounts down to 0.001 of a
 * unit."*
 *
 * The COLUMNS keep their own scales (quantity 3, unit cost 4): a two-place value
 * fits both, and other writers (stock, labour, purchase orders) still need the
 * wider ones. This is the scale of what a person types on an invoice, not of the
 * ledger.
 */
export const INVOICE_LINE_SCALE = 2;

/**
 * An invoice line figure as the entry form holds it: no sign, at most two
 * places, or null when it is not a number at all.
 *
 * ONE rule for every way a figure reaches the form, and the only one that turns
 * the receipt reader's `-1` into `1`. A line's sign comes from its KIND — the
 * server applies it, and `cost_entry_sign_chk` derives the accounting category
 * from it — so a minus sign on a quantity, a unit price or a total says nothing
 * the kind does not already say. The AI writes a discount as `-1 × 2470`; the
 * form takes `1 × 2470` on a Discount line, which posts the same credit.
 *
 * - A figure already in shape (unsigned, two places or fewer) is returned as it
 *   stands, so what somebody typed is never reformatted under them.
 * - Anything wider is rounded half away from zero (`roundDecimal`), the way the
 *   database rounds. A quantity then loses its trailing zeros ("1.000" reads
 *   "1", "1.500" reads "1.5"); a price keeps two places ("298.9700" reads
 *   "298.97"), because it is money.
 */
export function invoiceLineFigure(
  value: string | null | undefined,
  kind: "quantity" | "money",
): string | null {
  if (value === null || value === undefined) return null;
  const unsigned = value.trim().replace(/^[-+]/, "").trim();
  if (!/^\d+(\.\d+)?$/.test(unsigned)) return null;
  if (/^\d+(\.\d{1,2})?$/.test(unsigned)) return unsigned;
  const rounded = roundDecimal(unsigned, INVOICE_LINE_SCALE);
  if (rounded === null) return null;
  return kind === "quantity" ? rounded.replace(/0+$/, "").replace(/\.$/, "") : rounded;
}

function scaledBigIntOrNull(value: string, scale: number): bigint | null {
  const match = DECIMAL_RE.exec(value.trim());
  if (!match) return null;
  const [, sign, whole = "0", frac = ""] = match;
  if (frac.length > scale) return null;
  const scaled = BigInt(whole) * 10n ** BigInt(scale) + BigInt(frac.padEnd(scale, "0"));
  return sign === "-" ? -scaled : scaled;
}

/** Render integer cents back to a decimal string. */
export function fromCents(cents: number): string {
  if (!Number.isInteger(cents)) throw new Error(`cents must be an integer: ${cents}`);
  const negative = cents < 0;
  const abs = Math.abs(cents);
  const body = `${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, "0")}`;
  return negative ? `-${body}` : body;
}

/** Sum decimal strings exactly. `[]` sums to "0.00", not to 0 or "". */
export function sumAmounts(amounts: readonly string[]): string {
  let cents = 0;
  for (const a of amounts) cents += toCents(a);
  return fromCents(cents);
}

export function negateAmount(amount: string): string {
  return fromCents(-toCents(amount));
}

export function amountsEqual(a: string, b: string): boolean {
  return toCents(a) === toCents(b);
}

/** Normalise to the canonical two-place form ("5" -> "5.00", "-0" -> "0.00"). */
export function normaliseAmount(amount: string): string {
  return fromCents(toCents(amount));
}

const CURRENCY_PREFIX: Record<string, string> = { USD: "$", CAD: "CA$", EUR: "€", GBP: "£" };

/**
 * Display form: `-$1,234.50`. The sign leads the symbol rather than being
 * dropped or parenthesised, because credits and reversals are first-class here
 * and a negative that reads as a positive is the failure mode to avoid.
 */
export function formatMoney(amount: string, currency = "USD"): string {
  const cents = toCents(amount);
  const symbol = CURRENCY_PREFIX[currency] ?? `${currency} `;
  const abs = Math.abs(cents);
  const whole = String(Math.floor(abs / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const frac = String(abs % 100).padStart(2, "0");
  return `${cents < 0 ? "-" : ""}${symbol}${whole}.${frac}`;
}

/** Whole-dollar form for KPI tiles, where cents are noise. Rounds half-up. */
export function formatMoneyRounded(amount: string, currency = "USD"): string {
  const cents = toCents(amount);
  const symbol = CURRENCY_PREFIX[currency] ?? `${currency} `;
  const dollars = Math.round(Math.abs(cents) / 100);
  const whole = String(dollars).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${cents < 0 ? "-" : ""}${symbol}${whole}`;
}

/**
 * A rate, trimmed to the shortest form that does not lose precision.
 *
 * `posted_rate` is `numeric(10,4)`, so `$50.0000` and `$50.1250` both arrive with
 * four places. Printing the trailing zeros on a whole rate is noise; dropping the
 * real sub-cent digits on `50.1250` would be a lie. So trailing zeros go only down
 * to two places.
 */
export function formatRate(rate: string, currency = "USD", suffix = "/h"): string {
  const units = toRateUnits(rate);
  const symbol = CURRENCY_PREFIX[currency] ?? `${currency} `;
  const abs = Math.abs(units);
  const whole = String(Math.floor(abs / 10_000)).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  let frac = String(abs % 10_000).padStart(4, "0");
  // Strip trailing zeros, but never below two places: "5000" -> "50" (a whole
  // rate reads as dollars and cents), "1250" -> "125" (the half-cent is real and
  // stays), "1234" -> "1234" (nothing to strip).
  while (frac.length > 2 && frac.endsWith("0")) frac = frac.slice(0, -1);
  return `${units < 0 ? "-" : ""}${symbol}${whole}.${frac}${suffix}`;
}

/** A quantity, trailing zeros trimmed. `numeric(12,3)`, so "2.000" reads "2". */
export function formatQuantity(quantity: string): string {
  const units = toQuantityUnits(quantity);
  const abs = Math.abs(units);
  const whole = Math.floor(abs / 1000);
  const frac = String(abs % 1000).padStart(3, "0").replace(/0+$/, "");
  return `${units < 0 ? "-" : ""}${whole}${frac ? `.${frac}` : ""}`;
}

/**
 * "0.25 h × $50.00 = $12.50" — the time-card calc line.
 *
 * `postedValue` is the **ledger's own amount** for this entry, and when there is
 * one it is what gets printed. That is not a nicety: a posted entry is priced
 * through a `numeric(12,3)` quantity, so `minutes / 60` is rounded to three
 * decimals BEFORE the multiply, and re-deriving the total here from minutes and
 * the rate disagrees with `cost_entry.amount` by a cent on any minute value that
 * is not a multiple of three — 960 of the 1440 of them. Two cards on one screen
 * then print two totals for the same work (TRBUD-87). Whether the ledger should
 * round differently is a separate question; a caller computing its own total is
 * wrong either way.
 *
 * With no `postedValue` — the Log-time dialog's live preview, where nothing is
 * posted yet — the estimate is computed here. The rate is a **scale-4** value and
 * the result is a **scale-2** amount, so that arithmetic runs in ten-thousandths
 * and rounds once, at the end, to cents; doing it in floating point would
 * misstate a long shift at an odd rate.
 */
export function formatLabourCalc(
  minutes: number,
  rate: string | null,
  currency = "USD",
  postedValue: string | null = null,
): string {
  const hours = (minutes / 60).toFixed(2).replace(/\.?0+$/, "");
  if (rate === null) return `${hours} h`;
  // minutes × ten-thousandths ÷ (60 min × 100) = cents, rounded once.
  const value = postedValue ?? fromCents(Math.round((minutes * toRateUnits(rate)) / 6_000));
  return `${hours} h × ${formatRate(rate, currency, "")} = ${formatMoney(value, currency)}`;
}

/** Minutes to "3h 26m". Used for downtime chips and elapsed labels. */
export function formatDuration(minutes: number): string {
  const total = Math.max(0, Math.round(minutes));
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/** Minutes to decimal hours as a string, for the "2.5 h" columns. */
export function formatHours(minutes: number): string {
  return `${(minutes / 60).toFixed(1)} h`;
}
